import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFavouriteProjects } from '@/hooks/use-favourite-projects';
import { useProjects } from '@/hooks/use-projects';
import { formatRelativeTime } from '@/utils/format-time';

import type { ProjectListItem } from '@/types/project';

/**
 * Filters projects by case-insensitive substring match on projectKey or displayName.
 * Returns all projects when query is empty. Requires at least 1 character to filter.
 */
export function filterProjects(
  projects: ProjectListItem[],
  query: string,
): ProjectListItem[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return projects;
  }
  const lowerQuery = trimmed.toLowerCase();
  return projects.filter(
    (project) =>
      project.projectKey.toLowerCase().includes(lowerQuery) ||
      project.displayName.toLowerCase().includes(lowerQuery),
  );
}

export function ProjectListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useProjects();
  const { isFavourite, toggleFavourite } = useFavouriteProjects();

  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    return filterProjects(data.projects, searchQuery);
  }, [data?.projects, searchQuery]);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchQuery(e.target.value);
  }

  function handleCardClick(projectKey: string) {
    navigate(`/projects/${encodeURIComponent(projectKey)}`);
  }

  function handleCardKeyDown(e: React.KeyboardEvent, projectKey: string) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(projectKey);
    }
  }

  return (
    <main id="main-content" className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Projects
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Browse and search your projects
        </p>
      </div>

      {/* Search input */}
      <div className="mb-4">
        <label htmlFor="project-search" className="sr-only">
          Search projects
        </label>
        <input
          id="project-search"
          type="search"
          placeholder="Search projects by key or name..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          aria-label="Search projects by key or name"
        />
      </div>

      {/* Loading state */}
      {isLoading && <ProjectListSkeleton />}

      {/* Error state */}
      {isError && (
        <div
          role="alert"
          className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
        >
          <p className="font-medium">Failed to load projects</p>
          <p className="text-sm mt-1">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 text-sm font-medium rounded-lg bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Project card grid */}
      {!isLoading && !isError && (
        <>
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {searchQuery
                  ? `No projects match "${searchQuery}"`
                  : 'No projects found'}
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              aria-label="Project list"
              role="list"
            >
              {filteredProjects.map((project) => (
                <div
                  key={project.projectKey}
                  role="listitem"
                  tabIndex={0}
                  onClick={() => handleCardClick(project.projectKey)}
                  onKeyDown={(e) => handleCardKeyDown(e, project.projectKey)}
                  className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                      {project.projectKey}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavourite(project.projectKey);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        aria-label={isFavourite(project.projectKey) ? `Remove ${project.displayName} from favourites` : `Add ${project.displayName} to favourites`}
                        aria-pressed={isFavourite(project.projectKey)}
                        className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {isFavourite(project.projectKey) ? (
                          <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 text-gray-400 hover:text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        )}
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {project.repositoryCount}{' '}
                        {project.repositoryCount === 1 ? 'repo' : 'repos'}
                      </span>
                    </div>
                  </div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {project.displayName}
                  </h2>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {project.lastActivityAt
                      ? `Active ${formatRelativeTime(project.lastActivityAt)}`
                      : 'No recent activity'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </main>
  );
}

/** Loading skeleton for the project list */
function ProjectListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading projects">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 animate-pulse"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="h-5 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-5 w-36 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="mt-2 h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}
